<script>
import { useVuelidate } from '@vuelidate/core';
import { required, url } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';

// Ícones oferecidos p/ o item do menu principal. Precisam ser strings LITERAIS:
// o plugin de ícones (JIT) só gera o CSS de classes que aparecem no código-fonte,
// então essa lista é a fonte da verdade e garante que qualquer escolha renderize.
const SIDEBAR_ICONS = [
  'i-lucide-layout-grid',
  'i-lucide-globe',
  'i-lucide-link',
  'i-lucide-external-link',
  'i-lucide-book-open',
  'i-lucide-newspaper',
  'i-lucide-bar-chart-3',
  'i-lucide-line-chart',
  'i-lucide-pie-chart',
  'i-lucide-trending-up',
  'i-lucide-bell',
  'i-lucide-star',
  'i-lucide-heart',
  'i-lucide-calendar',
  'i-lucide-folder',
  'i-lucide-box',
  'i-lucide-package',
  'i-lucide-shopping-cart',
  'i-lucide-credit-card',
  'i-lucide-users',
  'i-lucide-briefcase',
  'i-lucide-file-text',
  'i-lucide-clipboard-list',
  'i-lucide-settings',
  'i-lucide-wrench',
  'i-lucide-zap',
  'i-lucide-rocket',
  'i-lucide-map-pin',
  'i-lucide-phone',
  'i-lucide-mail',
  'i-lucide-message-square',
  'i-lucide-video',
  'i-lucide-image',
  'i-lucide-database',
  'i-lucide-cloud',
  'i-lucide-shield',
  'i-lucide-gauge',
  'i-lucide-tv',
];

const DEFAULT_SIDEBAR_ICON = 'i-lucide-layout-grid';

export default {
  components: {
    NextButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    selectedAppData: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close'],
  setup() {
    return { v$: useVuelidate() };
  },
  validations: {
    app: {
      title: { required },
      content: {
        type: { required },
        url: { required, url },
      },
    },
  },
  data() {
    return {
      isLoading: false,
      sidebarIcons: SIDEBAR_ICONS,
      app: {
        title: '',
        content: {
          type: 'frame',
          url: '',
          show_in_sidebar: false,
          sidebar_icon: DEFAULT_SIDEBAR_ICON,
        },
      },
    };
  },
  computed: {
    header() {
      return this.$t(`INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.HEADER`);
    },
    submitButtonLabel() {
      return this.$t(
        `INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.FORM_SUBMIT`
      );
    },
  },
  mounted() {
    if (this.mode === 'UPDATE' && this.selectedAppData) {
      this.app.title = this.selectedAppData.title;
      // apps antigos não têm a flag → default false p/ manter reatividade do checkbox
      this.app.content = {
        show_in_sidebar: false,
        sidebar_icon: DEFAULT_SIDEBAR_ICON,
        ...this.selectedAppData.content[0],
      };
    }
  },
  methods: {
    selectIcon(icon) {
      this.app.content.sidebar_icon = icon;
    },
    closeModal() {
      // Reset the data once closed
      this.app = {
        title: '',
        content: {
          type: 'frame',
          url: '',
          show_in_sidebar: false,
          sidebar_icon: DEFAULT_SIDEBAR_ICON,
        },
      };
      this.$emit('close');
    },
    async submit() {
      try {
        this.v$.$touch();
        if (this.v$.$invalid) {
          return;
        }

        const action = this.mode.toLowerCase();
        const payload = {
          title: this.app.title,
          content: [this.app.content],
        };

        if (action === 'update') {
          payload.id = this.selectedAppData.id;
        }

        this.isLoading = true;
        await this.$store.dispatch(`dashboardApps/${action}`, payload);
        useAlert(
          this.$t(
            `INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.API_SUCCESS`
          )
        );
        this.closeModal();
      } catch (err) {
        useAlert(
          this.$t(`INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.API_ERROR`)
        );
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<template>
  <woot-modal :show="show" :on-close="closeModal">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="header" />
      <form class="w-full" @submit.prevent="submit">
        <woot-input
          v-model="app.title"
          :class="{ error: v$.app.title.$error }"
          class="w-full"
          :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_LABEL')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_PLACEHOLDER')
          "
          :error="
            v$.app.title.$error
              ? $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_ERROR')
              : null
          "
          data-testid="app-title"
          @input="v$.app.title.$touch"
          @blur="v$.app.title.$touch"
        />
        <woot-input
          v-model="app.content.url"
          :class="{ error: v$.app.content.url.$error }"
          class="w-full"
          :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_LABEL')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_PLACEHOLDER')
          "
          :error="
            v$.app.content.url.$error
              ? $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_ERROR')
              : null
          "
          data-testid="app-url"
          @input="v$.app.content.url.$touch"
          @blur="v$.app.content.url.$touch"
        />
        <label
          class="flex items-start w-full gap-2 py-2 cursor-pointer select-none"
        >
          <input
            v-model="app.content.show_in_sidebar"
            type="checkbox"
            class="mt-0.5 shrink-0"
            data-testid="app-show-in-sidebar"
          />
          <span class="flex flex-col">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SIDEBAR_LABEL') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SIDEBAR_HELP') }}
            </span>
          </span>
        </label>
        <div
          v-if="app.content.show_in_sidebar"
          class="flex flex-col w-full mb-2"
        >
          <span class="mb-1 text-sm font-medium text-n-slate-12">
            {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.ICON_LABEL') }}
          </span>
          <div
            class="grid grid-cols-8 gap-1.5 p-2 border rounded-lg border-n-weak max-h-40 overflow-y-auto"
          >
            <button
              v-for="icon in sidebarIcons"
              :key="icon"
              type="button"
              :title="icon.replace('i-lucide-', '')"
              :data-testid="`icon-${icon}`"
              class="flex items-center justify-center rounded-md aspect-square hover:bg-n-alpha-2"
              :class="
                app.content.sidebar_icon === icon
                  ? 'bg-n-brand/10 ring-1 ring-n-brand text-n-brand'
                  : 'text-n-slate-11'
              "
              @click.prevent="selectIcon(icon)"
            >
              <span :class="icon" class="size-4" />
            </button>
          </div>
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.CREATE.FORM_CANCEL')
            "
            @click.prevent="closeModal"
          />
          <NextButton
            type="submit"
            :label="submitButtonLabel"
            :disabled="v$.$invalid"
            :is-loading="isLoading"
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>
